import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { useTheme, lighten } from "@mui/material/styles";
import { PayrollInputDTO, PayrollInputRef, PayrollInputSTO } from "../../models/payroll-inputs";
import { CompanyLegalEntityRef } from "../../models/company-accounts/company-legal-entities";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { CompanyLegalEntitiesApiService } from "../../services/company-legal-entities";
import { Button, Chip, Dialog, DialogContent, Divider, Grid, TextField, Typography } from "@mui/material";
import AddNewDocument from "../AddNewDocument/AddNewDocument";
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPES,
  DOCUMENTS_STATUS_TYPES,
  PAYROLL_ENTRY_STATUS,
} from "../../utils/constants/constants";
import { DocumentCategoryRef } from "../../models/document-categories";
import { DocumentTypeRef } from "../../models/document-types";
import { Period, PeriodType } from "../../models/documents/period";
import { PayrollEntryStatusRef } from "../../models/payroll-entry-statuses";
import DialogTitle from "../DialogTitle/DialogTitle";
import AddEditButton from "../../reusable-components/AddEditButton/AddEditButton";
import AttachedDocuments from "../AttachedDocuments/AttachedDocuments";
import DialogActions from "../DialogActions/DialogActions";
import { ClientManagerPayrollInputsApiService } from "../../services/payroll-inputs";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import { DocumentDTO, DocumentRef } from "../../models/documents";
import { PayrollInputDocumentRef } from "../../models/payroll-input-documents/payroll-input-document-ref";
import { PayrollEntryTypesApiService } from "../../services/payroll-entry-types/payroll-entry-types-api.service";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

interface Props {
  effect: "add" | "edit";
  item?: PayrollInputDTO;
  handleNotifyParent?: () => void;
  defaultLegalEntity?: CompanyLegalEntityRef;
}

interface State {
  open: boolean;
  loading: boolean;
  error: string;
  item: PayrollInputDTO | PayrollInputSTO;
  isCCDOpen: boolean;
  isReadOnly: boolean;
}

const AddEditClientManagerPayrollInput = ({ effect, item, handleNotifyParent, defaultLegalEntity }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_client_payroll_input";
  const theme = useTheme();

  const DEFAULT_ITEM: PayrollInputSTO = useMemo(
    () => ({
      id: uuid(),
      isDeleted: false,
      legalEntity: (defaultLegalEntity as CompanyLegalEntityRef) ?? null,
      companyAccount: defaultLegalEntity?.companyAccount ?? null,
      employee: "",
      description: "",
      inputType: null,
      status: PAYROLL_ENTRY_STATUS.Pending as PayrollEntryStatusRef,
      payrollInputDocuments: [],
      payrollRun: null,
      isVerified: false,
    }),
    [defaultLegalEntity]
  );

  const [state, setState] = useState<State>({
    open: false,
    loading: false,
    error: "",
    item: item ?? (DEFAULT_ITEM as PayrollInputSTO),
    isCCDOpen: false,
    isReadOnly: !!item,
  });

  const handleChange = useCallback((field: keyof PayrollInputDTO, value: PayrollInputDTO[keyof PayrollInputDTO]) => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        [field]: value,
        ...(field === "legalEntity" && {
          companyAccount: (value as CompanyLegalEntityRef)?.companyAccount ?? null,
        }),
      },
    }));
  }, []);

  const handleLinkDocument = useCallback(
    (document: DocumentDTO | null) => {
      if (document) {
        setState((state) => ({
          ...state,
          item: {
            ...state.item,
            payrollInputDocuments: [
              ...state.item.payrollInputDocuments,
              {
                isConstant: false,
                isDeleted: false,
                id: uuid(),
                companyAccount: state.item.legalEntity?.companyAccount ?? null,
                payrollInput: state.item as PayrollInputRef,
                document: document as DocumentRef,
              } as PayrollInputDocumentRef,
            ],
          },
        }));
      }
    },
    [state.item]
  );

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new ClientManagerPayrollInputsApiService().saveItems([
        {
          ...state.item,
        },
      ]);
      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else {
        handleNotifyParent && handleNotifyParent();
        setState((state) => ({
          ...state,
          open: false,
          item: item ?? DEFAULT_ITEM,
          error: "",
        }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.item, handleNotifyParent, DEFAULT_ITEM, item]);

  useEffect(() => {
    !item &&
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          legalEntity: defaultLegalEntity as CompanyLegalEntityRef,
          companyAccount: defaultLegalEntity?.companyAccount ?? null,
        },
      }));
  }, [defaultLegalEntity, item]);

  return (
    <>
      <AddEditButton
        title="add"
        effect={effect}
        onClick={() => setState((state) => ({ ...state, open: true }))}
        {...(effect === "edit" && {
          customStyle: {
            paddingLeft: "25px",
            color: theme.palette.text.primary,
          },
        })}
      >
        {state.isReadOnly && <Typography>{`${t(`${tRP}.view`)}`}</Typography>}
      </AddEditButton>

      <Dialog open={state.open}>
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() =>
            setState((state) => ({ ...state, ...(state.isReadOnly ? { open: false } : { isCCDOpen: true }) }))
          }
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Grid style={{ display: "flex", gap: "15px", flexDirection: "column" }}>
              <Grid style={{ display: "flex", gap: "15px", marginTop: "15px" }}>
                <Grid style={{ display: "flex", gap: "15px", flexDirection: "column" }}>
                  <AutocompleteDropdown
                    labelKey={`${t(`common_words.legal_entity`)}`}
                    selectedValue={state.item.legalEntity}
                    field={"legalEntity"}
                    serviceApi={new CompanyLegalEntitiesApiService()}
                    handleChange={handleChange}
                    disabled={state.isReadOnly}
                  />
                  <TextField
                    label={`${t(`common_words.description`)}`}
                    value={state.item.description}
                    onChange={({ currentTarget }) => handleChange("description", currentTarget.value)}
                    rows={2}
                    minRows={2}
                    disabled={state.isReadOnly}
                  />
                  <AutocompleteDropdown
                    labelKey={`${t(`${tRP}.fields.type`)}`}
                    selectedValue={state.item.inputType}
                    serviceApi={new PayrollEntryTypesApiService()}
                    field={"inputType"}
                    handleChange={handleChange}
                    disabled={state.isReadOnly}
                  />
                </Grid>
                <Grid style={{ display: "flex", gap: "15px", flexDirection: "column" }}>
                  <TextField
                    label={`${t(`common_words.employee`)}`}
                    value={state.item.employee}
                    onChange={({ target }) => handleChange("employee", target.value)}
                    disabled={state.isReadOnly}
                  />
                  <Grid>
                    <Divider>
                      <Chip
                        label={`${t(`${tRP}.fields.documents`)}`}
                        style={{ color: lighten(theme.palette.secondary.light, 0.4) }}
                      />
                    </Divider>
                  </Grid>
                  <Grid style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                    {state.item.payrollInputDocuments.length > 0 && (
                      <AttachedDocuments
                        documentIDs={state.item.payrollInputDocuments.map(({ document }) => document?.id!)}
                        count={state.item.payrollInputDocuments.length}
                        documentIsReadOnly={state.isReadOnly}
                        useSimpleView
                        documentsNames={state.item?.payrollInputDocuments?.map((el) => el?.document?.name ?? "")}
                        buttonSize="2x"
                      />
                    )}
                    {!state.isReadOnly && (
                      <AddNewDocument
                        buttonType="plusButton"
                        documentFieldsProps={{
                          legalEntity: state.item.legalEntity,
                          period: Period.makePeriod(
                            new Date().getFullYear(),
                            PeriodType.Month,
                            new Date().getMonth() + 1
                          ),
                          status: DOCUMENTS_STATUS_TYPES.PENDING,
                          category: DOCUMENT_CATEGORIES.PAYROLL_INPUT as DocumentCategoryRef,
                          type: DOCUMENT_TYPES.PayrollInputAdHoc as DocumentTypeRef,
                        }}
                        readOnlyFields={{
                          isCategoryRO: true,
                          isLegalEntityRO: true,
                          isTypeRO: true,
                          isStatusRO: true,
                          isPeriodRO: true,
                        }}
                        hideReadOnlyFields
                        useMiniPlusButton
                        handleNotifyParent={handleLinkDocument}
                        closeAfterSave
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
              {state.isReadOnly && (
                <Button
                  style={{ alignSelf: "flex-end" }}
                  variant="outlined"
                  onClick={() => {
                    setState((state) => ({ ...state, open: false }));
                  }}
                  // color={theme.palette.actions.cancel as string}
                >
                  Close
                </Button>
              )}
            </Grid>
          </LoadingOverlay>
        </DialogContent>
        {!state.isReadOnly && (
          <DialogActions
            handleCancel={() => setState((state) => ({ ...state, isCCDOpen: true }))}
            handleSave={handleSave}
          />
        )}
      </Dialog>
      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState((state) => ({ ...state, isCCDOpen: false }))}
        handleDiscard={() =>
          setState((state) => ({ ...state, item: item ?? DEFAULT_ITEM, open: false, isCCDOpen: false, error: "" }))
        }
      />
    </>
  );
};

export default AddEditClientManagerPayrollInput;
